import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  Package,
  Clock,
  ChevronLeft,
  ChevronRight,
  User,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import { Motion } from '@/components/ui/motion'
import Header from '@/components/Header'
import BottomNavigation from '@/components/BottomNavigation'
import { useProjectsInstaller, InstallerProject } from '@/hooks/useProjectsInstaller'
import { useAuthStore } from '@/store/auth.store'
import { useTranslation } from '@/hooks/useTranslation'
import { Project } from '@/types/Project'

const InstallerDashboard: FC = () => {
  const navigate = useNavigate()
  const { user, isInstaller } = useAuthStore()
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(0)
  const [invitationsPage, setInvitationsPage] = useState(0)

  // Responsive items per page
  const [projectsPerPage, setProjectsPerPage] = useState(4)
  const [invitationsPerPage, setInvitationsPerPage] = useState(4)

  const { currentProjects, invitations, completedProjects, loading, error, updateProjectStatus, refresh } =
    useProjectsInstaller()

  useEffect(() => {
    // Redirect if user is not an installer
    if (user && !isInstaller) {
      navigate('/go')
      return
    }

    // Set responsive items per page
    const updateItemsPerPage = () => {
      const isMobile = window.innerWidth < 640
      setProjectsPerPage(isMobile ? 2 : 4)
      setInvitationsPerPage(isMobile ? 2 : 4)
    }

    updateItemsPerPage()
    window.addEventListener('resize', updateItemsPerPage)
    return () => window.removeEventListener('resize', updateItemsPerPage)
  }, [user, navigate, isInstaller])

  const handleProjectClick = (project: InstallerProject) => {
    navigate(`/project/${project.project?.id}`)
  }

  const handleAcceptInvitation = async (id: string) => {
    try {
      await updateProjectStatus(id, 'accepted')
    } catch (error) {
      console.error('Failed to accept invitation:', error)
      // TODO: Show error toast
    }
  }

  const handleRejectInvitation = async (id: string) => {
    try {
      await updateProjectStatus(id, 'rejected')
    } catch (error) {
      console.error('Failed to reject invitation:', error)
      // TODO: Show error toast
    }
  }

  const formatDate = (dateString: Date | string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60))
        return diffMinutes <= 1 ? t('TIME_JUST_NOW') : `${diffMinutes} ${t('TIME_MINUTES_AGO')}`
      }
      return diffHours === 1 ? t('TIME_HOUR_AGO') : `${diffHours} ${t('TIME_HOURS_AGO')}`
    } else if (diffDays === 1) {
      return t('TIME_YESTERDAY')
    } else if (diffDays < 7) {
      return `${diffDays} ${t('TIME_DAYS_AGO')}`
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} ${t('TIME_WEEKS_AGO')}`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className='h-4 w-4 text-green-500' />
      case 'rejected':
        return <XCircle className='h-4 w-4 text-red-500' />
      case 'pending':
        return <AlertCircle className='h-4 w-4 text-yellow-500' />
      default:
        return <Clock className='h-4 w-4 text-gray-500' />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'accepted':
        return t('STATUS_ACCEPTED')
      case 'rejected':
        return t('STATUS_REJECTED')
      case 'pending':
        return t('STATUS_PENDING')
      case 'completed':
        return t('STATUS_COMPLETED')
      case 'in_progress':
        return t('STATUS_IN_PROGRESS')
      case 'cancelled':
        return t('STATUS_CANCELLED')
      case 'delayed':
        return t('STATUS_DELAYED')
      default:
        return status
    }
  }

  // Get recent projects (last 30 days) - deduplicate by id
  const recentProjects = [...currentProjects, ...completedProjects]
    .filter((project, index, array) => {
      // Remove duplicates by id
      return array.findIndex(p => p.id === project.id) === index
    })
    .filter(project => {
      const projectDate = new Date(project.updatedAt)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      return projectDate >= thirtyDaysAgo
    })
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

  // Pagination calculations
  const totalCurrentPages = Math.ceil(recentProjects.length / projectsPerPage)
  const totalInvitationPages = Math.ceil(invitations.length / invitationsPerPage)

  const paginatedCurrentProjects = recentProjects.slice(
    currentPage * projectsPerPage,
    (currentPage + 1) * projectsPerPage
  )

  const paginatedInvitations = invitations.slice(
    invitationsPage * invitationsPerPage,
    (invitationsPage + 1) * invitationsPerPage
  )

  const getEndCustomer = (project: InstallerProject) => {
    return project.project?.businessName || (project.project?.customerFirstName && project.project?.customerLastName)
      ? `${project.project.customerFirstName} ${project.project.customerLastName}`
      : ''
  }

  if (loading) {
    return (
      <div className='min-h-screen bg-[hsl(var(--background))] pb-24'>
        <Header title={t('INSTALLER_DASHBOARD')} />
        <div className='container mx-auto px-4 py-6'>
          <div className='flex items-center justify-center py-12'>
            <div className='text-center'>
              <div className='flex flex-col items-center space-y-4 mb-4'>
                <img src='/logo_posacheck.png' alt='Posacheck Logo' className='w-24 h-24 object-contain' />
                <span className='spinner spinner-lg' style={{ borderTopColor: 'var(--brand-red)' }} />
              </div>
              <p>{t('INSTALLER_LOADING')}</p>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    )
  }

  if (error) {
    return (
      <div className='min-h-screen bg-[hsl(var(--background))] pb-24'>
        <Header title={t('INSTALLER_DASHBOARD')} />
        <div className='container mx-auto px-4 py-6'>
          <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'>
            <div className='flex items-center justify-center p-12'>
              <div className='text-center'>
                <XCircle className='mx-auto h-12 w-12 mb-4 text-red-500' />
                <h4 className='text-xl mb-2'>{t('INSTALLER_ERROR_TITLE')}</h4>
                <p className='text-sm text-[hsl(var(--muted-foreground))] mb-4'>{error}</p>
                <button className='h-10 px-4 rounded-[var(--radius)] bg-[hsl(var(--primary))] text-white text-sm font-medium hover:bg-[hsl(var(--primary))]/90 transition-colors inline-flex items-center gap-2' onClick={refresh}>
                  {t('INSTALLER_TRY_AGAIN')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-[hsl(var(--background))] pb-24'>
      <Header title={t('INSTALLER_DASHBOARD')} />
      <div className='container mx-auto px-4 py-6'>
        <Motion variant='fadeIn' duration={500}>
          {/* Project Invitations Section */}
          {invitations.length > 0 && (
            <div className='mb-8'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-2xl font-semibold'>{t('INSTALLER_PROJECT_INVITATIONS')}</h2>
                {invitations.length > invitationsPerPage && (
                  <div className='flex items-center gap-2'>
                    <button
                      className='p-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50'
                      onClick={() => setInvitationsPage(Math.max(0, invitationsPage - 1))}
                      disabled={invitationsPage === 0}
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </button>
                    <span className='text-sm'>
                      {invitationsPage + 1} / {totalInvitationPages}
                    </span>
                    <button
                      className='p-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50'
                      onClick={() => setInvitationsPage(Math.min(totalInvitationPages - 1, invitationsPage + 1))}
                      disabled={invitationsPage === totalInvitationPages - 1}
                    >
                      <ChevronRight className='h-4 w-4' />
                    </button>
                  </div>
                )}
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4'>
                {paginatedInvitations.map((invitation, index) => (
                  <Motion key={invitation.id} variant='slideUp' duration={300} delay={index * 100}>
                    <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'>
                      <div className='p-5'>
                        <div className='flex items-start justify-between mb-4'>
                          <div className='flex-1'>
                            <div className='flex items-center gap-2 mb-3'>
                              <Building2 className='h-5 w-5 text-[var(--brand-red)]' />
                              <span className='text-sm font-medium'>{t('INSTALLER_NEW_PROJECT_INVITATION')}</span>
                              <span className='text-xs text-[hsl(var(--muted-foreground))]'>
                                • {formatDate(invitation.invitationDate || invitation.createdAt)}
                              </span>
                            </div>

                            <h4 className='text-lg font-semibold mb-2'>
                              {invitation.project?.projectNumber || t('INSTALLER_PROJECT_DEFAULT')}
                            </h4>

                            {/* Customer Information */}
                            {(invitation.project?.customerFirstName ||
                              invitation.project?.customerLastName ||
                              invitation.project?.businessName) && (
                              <div className='text-sm mb-2'>
                                {invitation.project.businessName && (
                                  <p className='flex items-center gap-1 mb-1'>
                                    <User className='h-3 w-3' />
                                    {invitation.project.businessName}
                                  </p>
                                )}
                                {(invitation.project.customerFirstName || invitation.project.customerLastName) && (
                                  <p className='flex items-center gap-1 mb-1'>
                                    <User className='h-3 w-3' />
                                    {[invitation.project.customerFirstName, invitation.project.customerLastName]
                                      .filter(Boolean)
                                      .join(' ')}
                                  </p>
                                )}
                                {/* Future fields - show when available */}
                                {invitation.project?.contractor && (
                                  <p className='flex items-center gap-1 mb-1 text-blue-600'>
                                    <User className='h-3 w-3' />
                                    {t('CONTRACTOR_LABEL')}: {invitation.project.contractor}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Site Address with Commessa prefix */}
                            {(invitation.project?.address || invitation.project?.city) && (
                              <p className='text-sm mb-2 flex items-center gap-1'>
                                <MapPin className='h-3 w-3' />
                                <span className='font-medium'>{t('SITE_ADDRESS_LABEL')}</span>
                                <span>
                                  {[invitation.project.address, invitation.project.city, invitation.project.province]
                                    .filter(Boolean)
                                    .join(', ')}
                                </span>
                              </p>
                            )}

                            <p className='text-xs text-[hsl(var(--muted-foreground))]'>ID: {invitation.id}</p>
                          </div>
                        </div>

                        <div className='flex gap-2'>
                          <button
                            className='flex-1 h-9 rounded-[var(--radius)] bg-[hsl(var(--success))] text-white text-sm font-medium hover:bg-[hsl(var(--success))]/90 transition-colors'
                            onClick={() => handleAcceptInvitation(invitation.id)}
                          >
                            {t('INSTALLER_ACCEPT')}
                          </button>
                          <button
                            className='flex-1 h-9 rounded-[var(--radius)] border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] text-sm font-medium hover:bg-[hsl(var(--destructive))]/10 transition-colors'
                            onClick={() => handleRejectInvitation(invitation.id)}
                          >
                            {t('INSTALLER_REJECT')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Motion>
                ))}
              </div>
            </div>
          )}

          {/* Recent Projects Section */}
          <div>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-2xl font-semibold'>{t('INSTALLER_RECENT_PROJECTS')}</h2>
              {recentProjects.length > projectsPerPage && (
                <div className='flex items-center gap-2'>
                  <button
                    className='p-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50'
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  <span className='text-sm'>
                    {currentPage + 1} / {totalCurrentPages}
                  </span>
                  <button
                    className='p-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50'
                    onClick={() => setCurrentPage(Math.min(totalCurrentPages - 1, currentPage + 1))}
                    disabled={currentPage === totalCurrentPages - 1}
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>

            {recentProjects.length === 0 ? (
              <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'>
                <div className='flex items-center justify-center p-12'>
                  <div className='text-center'>
                    <Package className='mx-auto h-12 w-12 mb-4 text-[hsl(var(--muted-foreground))]' />
                    <h4 className='text-xl'>{t('INSTALLER_NO_RECENT_PROJECTS')}</h4>
                    <p className='text-sm mt-2 text-[hsl(var(--muted-foreground))]'>{t('INSTALLER_PROJECTS_APPEAR_MESSAGE')}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4'>
                {paginatedCurrentProjects.map((project, index) => (
                  <Motion key={project.id} variant='slideUp' duration={300} delay={index * 100}>
                    <div
                      className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] cursor-pointer card-hover'
                      onClick={() => handleProjectClick(project)}
                    >
                      <div className='p-5'>
                        <div className='flex items-start justify-between'>
                          <div className='flex-1'>
                            <div className='flex items-center gap-2 mb-3'>
                              <Building2 className='h-5 w-5 text-[var(--brand-red)]' />
                              <span className='text-sm'>{getStatusText(project.status)}</span>
                              {getStatusIcon(project.status)}
                              <span className='text-xs text-[hsl(var(--muted-foreground))]'>• {formatDate(project.updatedAt)}</span>
                            </div>

                            <h4 className='text-lg font-semibold mb-2'>
                              {project.project?.projectNumber || t('INSTALLER_PROJECT_DEFAULT')}
                            </h4>

                            {/* Customer Information */}
                            {(project.project?.customerFirstName ||
                              project.project?.customerLastName ||
                              project.project?.businessName) && (
                              <div className='text-sm mb-2'>
                                {/* Future fields - show when available */}
                                {project.project?.contractor && (
                                  <p className='flex items-center gap-1 mb-1 text-blue-600'>
                                    <User className='h-3 w-3' />
                                    {t('CONTRACTOR_LABEL')}: {project.project.contractor}
                                  </p>
                                )}
                                {getEndCustomer(project) && (
                                  <p className='flex items-center gap-1 mb-1 text-green-600'>
                                    <User className='h-3 w-3' />
                                    {t('END_CUSTOMER_LABEL')}: {getEndCustomer(project)}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Site Address with Commessa prefix */}
                            {(project.project?.address || project.project?.city) && (
                              <p className='text-sm mb-2 flex items-center gap-1'>
                                <MapPin className='h-3 w-3' />
                                <span className='font-medium'>{t('SITE_ADDRESS_LABEL')}</span>
                                <span>
                                  {[project.project.address, project.project.city, `(${project.project.province})`]
                                    .filter(Boolean)
                                    .join(', ')}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Motion>
                ))}
              </div>
            )}
          </div>
        </Motion>
      </div>
      <BottomNavigation />
    </div>
  )
}

export default InstallerDashboard
